import { create } from 'zustand';
import * as shopApi from '../services/shopApi';
import { useToastStore } from './toastStore';

export interface WishlistProduct {
  id: number;
  sku: string;
  name: string;
  brand_name: string | null;
  price: number | string | null;  // Can be string from Decimal serialization
  compare_at_price: number | string | null;
  primary_image_url: string | null;
  available_to_sell: number;
  darkstore_status?: 'live' | 'coming_soon' | 'not_available';  // Optional for backwards compat
  added_at: string;
}

interface LikesState {
  // Local SKUs (used for guests and as quick lookup)
  likedSkus: string[];

  // Full product data from backend (for logged-in users)
  wishlistProducts: WishlistProduct[];

  // Loading state
  isLoading: boolean;
  isSyncing: boolean;

  // Actions
  toggleLike: (sku: string, productId?: number) => Promise<void>;
  isLiked: (sku: string) => boolean;
  clearLikes: () => void;

  // Backend sync actions
  fetchWishlist: () => Promise<void>;
  mergeAndSync: () => Promise<void>;

  // Internal state management
  setLikedSkus: (skus: string[]) => void;
}

// Helper to check if user is authenticated
const getAuthToken = (): string | null => {
  return localStorage.getItem('customer_access_token');
};

export const useLikesStore = create<LikesState>()(
  (set, get) => ({
    likedSkus: [],
    wishlistProducts: [],
    isLoading: false,
    isSyncing: false,

    toggleLike: async (sku: string, productId?: number) => {
      const token = getAuthToken();

      // Require authentication for likes
      if (!token) {
        // Return false to indicate auth required - caller should show login modal
        throw new Error('AUTH_REQUIRED');
      }

      const { likedSkus } = get();
      const isCurrentlyLiked = likedSkus.includes(sku);

      if (isCurrentlyLiked) {
        // Remove from likes - optimistic update
        set({ likedSkus: likedSkus.filter(s => s !== sku) });

        try {
          if (productId) {
            await shopApi.removeFromWishlist(productId);
          } else {
            // Need to find product ID from wishlist
            const product = get().wishlistProducts.find(p => p.sku === sku);
            if (product) {
              await shopApi.removeFromWishlist(product.id);
            }
          }
          // Update wishlist products
          set(state => ({
            wishlistProducts: state.wishlistProducts.filter(p => p.sku !== sku)
          }));
        } catch (error) {
          console.error('Failed to remove from wishlist:', error);
          useToastStore.getState().error('Failed to update wishlist');
          // Revert on error
          set({ likedSkus: [...likedSkus] });
          throw error;
        }
      } else {
        // Add to likes - optimistic update
        set({ likedSkus: [...likedSkus, sku] });

        try {
          await shopApi.addToWishlistBySku(sku);
        } catch (error) {
          console.error('Failed to add to wishlist:', error);
          useToastStore.getState().error('Failed to update wishlist');
          // Revert on error
          set({ likedSkus: likedSkus.filter(s => s !== sku) });
          throw error;
        }
      }
    },

    isLiked: (sku: string) => {
      return get().likedSkus.includes(sku);
    },

    clearLikes: () => {
      set({ likedSkus: [], wishlistProducts: [] });
    },

    fetchWishlist: async () => {
      const token = getAuthToken();
      if (!token) {
        // Clear any stale state when not authenticated
        set({ likedSkus: [], wishlistProducts: [], isLoading: false });
        return;
      }

      set({ isLoading: true });
      try {
        const response = await shopApi.getWishlist();
        const { items } = response.data;

        // Update both the SKUs list and product details
        set({
          likedSkus: items.map((item: WishlistProduct) => item.sku),
          wishlistProducts: items,
          isLoading: false
        });
      } catch (error) {
        console.error('Failed to fetch wishlist:', error);
        set({ likedSkus: [], wishlistProducts: [], isLoading: false });
      }
    },

    mergeAndSync: async () => {
      // No longer merging local likes - just fetch from backend
      await get().fetchWishlist();
    },

    setLikedSkus: (skus: string[]) => {
      set({ likedSkus: skus });
    },
  })
);
